import { InputProps } from '@mui/material'
import * as Yup from 'yup'

import { Form } from '../../../ana-toolbox'
import { InsuranceType } from '../../../ana-toolbox'
import { MaskedDateInput } from '../../components/masked-date-input/masked-date-input'
import { isPatientUnderaged } from '../entities/Form'
import { createFields } from '../helpers/createFields'
import { resetFields } from '../helpers/resetFields'
import { toBoolean } from '../helpers/toBoolean'
import { buildValidationSchema } from '../yup/buildValidationSchema'
import { adultValid, dateValidation, latinValidation } from '../yup/yup'

Yup.addMethod(Yup.string, 'adult', adultValid)
Yup.addMethod(Yup.string, 'birthday', dateValidation)
Yup.addMethod(Yup.string, 'latin', latinValidation)

const insuranceOptions = [
  { label: 'option.private_insurance', value: InsuranceType.PRIVATE },
  { label: 'option.social_insurance', value: InsuranceType.PUBLIC },
]

const yesNoOptions = [
  { label: 'yes', value: 'true' },
  { label: 'no', value: 'false' },
]

const assistanceBasisOptions = [
  { label: 'option.handicap', value: 'HANDICAP' },
  { label: 'option.limited_everyday_competence', value: 'LIMITED_EVERYDAY_COMPETENCE' },
]

const hasFamilyInsurance = (form: Partial<Form>) => toBoolean(form.hasFamilyInsurance)

const isPrivatelyInsured = (form: Partial<Form>) => form.type === InsuranceType.PRIVATE

const livesOnDifferentAddress = (form: Partial<Form>) =>
  hasFamilyInsurance(form) && toBoolean(form.livesOnDifferentAddress)

const hasDentalInsurance = (form: Partial<Form>) => toBoolean(form.hasDentalInsurance)

export const insuranceFields = (email?: string) =>
  createFields({
    hasCareLevel: {
      __type: 'radioGroup',
      label: 'field.hasCareLevel',
      name: 'hasCareLevel',
      options: yesNoOptions,
      isRequired: true,
      validation: Yup.string().required(),
      hooks: {
        beforeChange: (value, helpers, _event) => {
          if (value === 'false') {
            resetFields(['careLevelLimitedInTime'], helpers)
          }
        },
      },
    },
    careLevelLimitedInTime: {
      __type: 'radioGroup',
      label: 'field.careLevelLimitedInTime',
      name: 'careLevelLimitedInTime',
      options: yesNoOptions,
      isRequired: true,
      helperText: email ? 'field.careLevelLimitedInTimeEmail_helper' : 'field.careLevelLimitedInTime_helper',
      helperData: { email },
      visibleWhen: ({ hasCareLevel }) => hasCareLevel === 'true' || hasCareLevel === true,
      validation: Yup.mixed().when('hasCareLevel', {
        is: (value: string | boolean) => value === 'true' || value === true,
        then: Yup.mixed().required(),
        otherwise: Yup.mixed(),
      }),
    },
    entitledToIntegrationAssistance: {
      __type: 'radioGroup',
      label: 'field.entitledToIntegrationAssistance',
      name: 'entitledToIntegrationAssistance',
      options: yesNoOptions,
      isRequired: true,
      validation: Yup.string().required(),
      hooks: {
        beforeChange: (value, helpers, _event) => {
          if (value === 'false') {
            resetFields(['integrationAssistanceBasedOnType'], helpers)
          }
        },
      },
    },
    integrationAssistanceBasedOnType: {
      __type: 'radioGroup',
      label: 'field.integrationAssistanceBasedOnType',
      name: 'integrationAssistanceBasedOnType',
      isRequired: true,
      options: assistanceBasisOptions,
      visibleWhen: ({ entitledToIntegrationAssistance }) =>
        entitledToIntegrationAssistance === 'true' || entitledToIntegrationAssistance === true,
      helperText: email
        ? 'field.integrationAssistanceBasedOnTypeEmail_helper'
        : 'field.integrationAssistanceBasedOnType_helper',
      helperData: { email },
      validation: Yup.mixed().when('entitledToIntegrationAssistance', {
        is: (value: string | boolean) => value === 'true' || value === true,
        then: Yup.mixed().required(),
        otherwise: Yup.mixed(),
      }),
    },
    type: {
      __type: 'radioGroup',
      label: 'field.insurance_type',
      name: 'type',
      options: insuranceOptions,
      isRequired: true,
      validation: Yup.string().required('insurance_type_error'),
      helperText: 'field.insurance_type_helper',
      hooks: {
        beforeChange: (value: unknown, helpers, _event) => {
          if (typeof value !== 'string') return

          const birthday = helpers.getFieldProps<string>('birthday').value

          resetFields(
            ['insuranceCompany', 'insuranceCompanyId', 'dentalInsuranceCompany', 'dentalInsuranceCompanyId'],
            helpers
          )

          if (value === InsuranceType.PRIVATE) {
            helpers.setFieldValue('hasDentalInsurance', false)
          }

          if (value === InsuranceType.PUBLIC) {
            helpers.setFieldValue('hasCivilServiceAllowance', false)
          }

          if (isPatientUnderaged({ birthday })) {
            if (value === InsuranceType.PUBLIC) {
              // As specified in D21-7571, we need to show the family insurance fields expanded in case the patient is
              // publicly insured and is underaged
              helpers.setFieldValue('hasFamilyInsurance', true)
            } else {
              // As private insured underaged patient should not have family insurance, we need to reset all the fields
              // in case the patient has filled it while the toogle was in "public" mode.
              helpers.setFieldValue('hasFamilyInsurance', false)
              resetFields(
                ['holderFirstName', 'holderLastName', 'holderBirthday', 'holderCity', 'holderStreet', 'holderZipcode'],
                helpers
              )
            }
          }
        },
      },
    },
    insuranceCompany: {
      __type: 'custom',
      label: 'field.insurance_company_label',
      name: 'insuranceCompany',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when(['type'], {
          is: (type: InsuranceType) => type === InsuranceType.PRIVATE,
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
      helperText: 'field.insurance_company_helper',
      visibleWhen: isPrivatelyInsured,
    },
    hasFamilyInsurance: {
      __type: 'toggle',
      label: 'field.has_family_insurance_label',
      name: 'hasFamilyInsurance',
      inputProps: {
        withText: true,
      },
      validation: Yup.boolean(),
      helperText: 'field.family_insurance_helper',
      hooks: {
        beforeChange: (value, helpers, _event) => {
          if (typeof value !== 'boolean') return
          if (value) return

          // Reset the nested fields when the `hasFamilyInsurance` switched
          // to `false`.
          resetFields(
            [
              'holderFirstName',
              'holderLastName',
              'holderBirthday',
              'holderStreet',
              'holderCity',
              'holderZipcode',
              'livesOnDifferentAddress',
            ],
            helpers
          )
        },
      },
      visibleWhen: (form) => {
        const isUnderaged = isPatientUnderaged(form)
        const privatelyInsured = form.type === InsuranceType.PRIVATE

        // We should hide the family insurance toggle when the patient is underaged and is
        // privately insured. The family insurance only exists for publicly insured patients.
        return !(isUnderaged && privatelyInsured)
      },
    },
    holderFirstName: {
      __type: 'text',
      label: 'field.insurance_holder_first_name_v2',
      name: 'holderFirstName',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when('hasFamilyInsurance', {
          is: true,
          then: Yup.string().required('holder_first_name_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: hasFamilyInsurance,
    },
    holderLastName: {
      __type: 'text',
      label: 'field.insurance_holder_last_name_v2',
      name: 'holderLastName',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when('hasFamilyInsurance', {
          is: true,
          then: Yup.string().required('holder_last_name_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: hasFamilyInsurance,
    },
    holderBirthday: {
      __type: 'text',
      label: 'field.insurance_holder_date_of_birth_v2',
      name: 'holderBirthday',
      inputProps: {
        type: 'text',
        inputComponent: MaskedDateInput as InputProps['inputComponent'],
        InputLabelProps: {
          shrink: true,
        },
      },
      isRequired: true,
      validation: Yup.string().when('hasFamilyInsurance', {
        is: true,
        then: Yup.string()
          .birthday({ empty: 'birthday_form_error', invalidDate: 'invalid_date', inPast: 'in_past' })
          .adult('adult_age')
          .required(),
        otherwise: Yup.string(),
      }),
      visibleWhen: hasFamilyInsurance,
    },
    livesOnDifferentAddress: {
      __type: 'toggle',
      label: 'field.lives_on_different_address',
      name: 'livesOnDifferentAddress',
      inputProps: {
        withText: true,
      },
      validation: Yup.boolean(),
      visibleWhen: hasFamilyInsurance,
      helperText: 'field.lives_on_different_address_helper',
      hooks: {
        beforeChange: (_value, helpers, _event) => {
          resetFields(['holderStreet', 'holderCity', 'holderZipcode'], helpers)
        },
      },
    },
    holderCity: {
      __type: 'text',
      label: 'field.insurance_family_city_v2',
      name: 'holderCity',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when(['livesOnDifferentAddress', 'hasFamilyInsurance'], {
          is: (livesOnDifferentAddress, hasFamilyInsurance) => livesOnDifferentAddress && hasFamilyInsurance,
          then: Yup.string().required('holder_city_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: livesOnDifferentAddress,
    },
    holderStreet: {
      __type: 'text',
      label: 'field.insurance_family_street_and_number_v2',
      name: 'holderStreet',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when(['livesOnDifferentAddress', 'hasFamilyInsurance'], {
          is: (livesOnDifferentAddress, hasFamilyInsurance) => livesOnDifferentAddress && hasFamilyInsurance,
          then: Yup.string().required('holder_street_and_number_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: livesOnDifferentAddress,
    },
    holderZipcode: {
      __type: 'text',
      label: 'field.insurance_family_postal_code_v2',
      name: 'holderZipcode',
      isRequired: true,
      validation: Yup.string()
        .latin()
        .when(['livesOnDifferentAddress', 'hasFamilyInsurance'], {
          is: (livesOnDifferentAddress, hasFamilyInsurance) => livesOnDifferentAddress && hasFamilyInsurance,
          then: Yup.string().required('holder_zip_code_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: livesOnDifferentAddress,
    },
    hasDentalInsurance: {
      __type: 'toggle',
      label: 'field.supplementary_dental_insurance',
      name: 'hasDentalInsurance',
      inputProps: {
        withText: true,
      },
      validation: Yup.boolean(),
      helperText: 'field.supplementary_dental_insurance_helper',
      visibleWhen: ({ type }) => type === InsuranceType.PUBLIC,
      hooks: {
        beforeChange: (value, helpers, _event) => {
          if (value !== false) return

          resetFields(['dentalInsuranceCompany', 'dentalInsuranceCompanyId'], helpers)
        },
      },
    },
    dentalInsuranceCompany: {
      __type: 'custom',
      label: 'field.dental_insurance_company_label',
      name: 'dentalInsuranceCompany',
      inputProps: {
        type: 'text',
      },
      isRequired: true,
      helperText: 'field.dental_insurance_company_helper',
      validation: Yup.string()
        .latin()
        .when('hasDentalInsurance', {
          is: true,
          then: Yup.string().required('dental_insurance_company_name_error'),
          otherwise: Yup.string(),
        }),
      visibleWhen: hasDentalInsurance,
    },
    hasCivilServiceAllowance: {
      __type: 'toggle',
      label: 'field.has_civil_service_allowance_label',
      helperText: 'field.has_civil_service_allowance_helper',
      name: 'hasCivilServiceAllowance',
      inputProps: {
        withText: true,
      },
      visibleWhen: ({ type }) => type === InsuranceType.PRIVATE,
      validation: Yup.boolean(),
    },
  })

export const insuranceFieldsValidationSchema = (email?: string) => buildValidationSchema(insuranceFields(email))
